import { useRef, useState } from "react";
import { Bold, Italic, Heading1, Heading2, Heading3, List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon, Eye, Edit3 } from "lucide-react";
import { renderMarkdown } from "@/lib/blog";
import { toast } from "sonner";

const insert = (ref, setValue, before, after = "") => {
  const el = ref.current;
  if (!el) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  const sel = el.value.slice(start, end);
  const next = el.value.slice(0, start) + before + (sel || "") + after + el.value.slice(end);
  setValue(next);
  setTimeout(() => {
    el.focus();
    const pos = start + before.length + (sel ? sel.length + after.length : 0);
    el.selectionStart = el.selectionEnd = pos;
  }, 0);
};

const Btn = ({ onClick, title, children, testid }) => (
  <button type="button" title={title} data-testid={testid} onClick={onClick}
          className="flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100">
    {children}
  </button>
);

/**
 * Markdown editor with toolbar + live preview tab + inline image upload.
 * Stores raw markdown; blog pages render through marked + DOMPurify.
 */
export function MarkdownEditor({ value, onChange, onImageUpload, testid = "markdown-editor" }) {
  const ta = useRef(null);
  const [mode, setMode] = useState("edit");

  const addLink = () => {
    const url = window.prompt("Link URL");
    if (!url) return;
    insert(ta, onChange, "[", `](${url})`);
  };

  const addImage = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp,image/gif";
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      try {
        const path = await onImageUpload(f);
        insert(ta, onChange, `\n![Image](${process.env.REACT_APP_BACKEND_URL}/api/blog/image/${path})\n`);
      } catch (e) {
        toast.error("Image upload failed");
      }
    };
    input.click();
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 p-1">
        <Btn testid="md-h1" title="Heading 1" onClick={() => insert(ta, onChange, "\n# ")}><Heading1 className="h-4 w-4" /></Btn>
        <Btn testid="md-h2" title="Heading 2" onClick={() => insert(ta, onChange, "\n## ")}><Heading2 className="h-4 w-4" /></Btn>
        <Btn testid="md-h3" title="Heading 3" onClick={() => insert(ta, onChange, "\n### ")}><Heading3 className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Btn testid="md-bold" title="Bold" onClick={() => insert(ta, onChange, "**", "**")}><Bold className="h-4 w-4" /></Btn>
        <Btn testid="md-italic" title="Italic" onClick={() => insert(ta, onChange, "*", "*")}><Italic className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Btn testid="md-ul" title="Bulleted list" onClick={() => insert(ta, onChange, "\n- ")}><List className="h-4 w-4" /></Btn>
        <Btn testid="md-ol" title="Numbered list" onClick={() => insert(ta, onChange, "\n1. ")}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn testid="md-quote" title="Quote" onClick={() => insert(ta, onChange, "\n> ")}><Quote className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Btn testid="md-link" title="Link" onClick={addLink}><LinkIcon className="h-4 w-4" /></Btn>
        <Btn testid="md-image" title="Image" onClick={addImage}><ImageIcon className="h-4 w-4" /></Btn>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" data-testid="md-mode-edit" onClick={() => setMode("edit")}
                  className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${mode === "edit" ? "bg-slate-900 text-white" : "text-slate-600"}`}>
            <Edit3 className="h-3.5 w-3.5" /> Edit
          </button>
          <button type="button" data-testid="md-mode-preview" onClick={() => setMode("preview")}
                  className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${mode === "preview" ? "bg-slate-900 text-white" : "text-slate-600"}`}>
            <Eye className="h-3.5 w-3.5" /> Preview
          </button>
        </div>
      </div>
      {mode === "edit" ? (
        <textarea
          ref={ta}
          data-testid="md-textarea"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Write your article in Markdown. Use the toolbar for formatting."
          className="h-[520px] w-full resize-none rounded-b-md border-0 p-4 font-mono text-sm text-slate-900 focus:outline-none"
        />
      ) : (
        <div
          data-testid="md-preview"
          className="prose prose-slate h-[520px] max-w-none overflow-auto p-5"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
        />
      )}
    </div>
  );
}
